// Session-replay masking markers (PostHog / rrweb) — see the maskTextFn in
// src/lib/analytics.ts for the mechanism.
//
// Default posture: EVERY text node is masked in replays (COPPA — the
// learner's name renders across the app). Content surfaces whose text is
// book-derived or product copy (games, evaluation questions/results, book
// analysis, upload flow, guide previews) opt in to visible text by
// spreading PH_UNMASK on their root element.
//
// THE RULE THAT KEEPS THIS SAFE: any learner-name or other PII render
// inside an unmasked zone must be wrapped in <MaskedName> (or PH_MASK on
// an existing element). rrweb resolves the NEAREST marker, so the inner
// mask always wins. When adding a name render to an unmasked surface, use
// <MaskedName> — a raw {learner.name} inside an unmask zone ships the
// child's name to session replays.

import type { ReactNode } from 'react'

/** Spread on a content surface's root to make its replay text visible. */
export const PH_UNMASK = { 'data-ph-unmask': 'true' } as const

/** Spread on an element inside an unmasked zone to re-mask its text. */
export const PH_MASK = { 'data-ph-mask': 'true' } as const

/** Wraps a learner-name (or any PII) render inside an unmasked zone so it
 *  stays masked in session replays. Renders inline with no styling. */
export function MaskedName({ children }: { children: ReactNode }) {
  return <span {...PH_MASK}>{children}</span>
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** For pre-baked strings that interpolate the learner's name (e.g. the
 *  generated game descriptions): splits the text and masks each occurrence
 *  of the name, leaving the rest visible. Case-insensitive, whole string. */
export function MaskedNameText({ text, name }: { text: string; name?: string | null }) {
  if (!name?.trim()) return <>{text}</>
  const parts = text.split(new RegExp(`(${escapeRegExp(name)})`, 'gi'))
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === name.toLowerCase() ? (
          <span key={i} {...PH_MASK}>{part}</span>
        ) : (
          part
        ),
      )}
    </>
  )
}
